import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Space, Form, Input, InputNumber, Select, Switch, message, Image, Upload, Card, Row, Col, Checkbox, Tag, Typography } from 'antd';
import { PlusOutlined, StarOutlined, StarFilled, MinusCircleOutlined, ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { productAPI, categoryAPI, dropshipperAPI } from '../api';
import { compressImage, isImageFile, formatFileSize } from '../utils/imageCompression';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';

const { Option } = Select;
const { Text } = Typography;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const EditProduct = () => {
  const navigate = useNavigate();
  const { product_id } = useParams();
  const isEditMode = Boolean(product_id);
  
  const [categories, setCategories] = useState([]);
  const [freightChannels, setFreightChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [isSingleProduct, setIsSingleProduct] = useState(true);
  const [variants, setVariants] = useState([]);
  const [variantTypes, setVariantTypes] = useState([]);
  const [variantLevels, setVariantLevels] = useState([]);
  const [variantImages, setVariantImages] = useState({}); // 存储variant图片 {variantIndex: fileObject}
  const [existingVariantImages, setExistingVariantImages] = useState({}); // 存储现有的variant图片 {variantIndex: imageData}
  const [descriptionContent, setDescriptionContent] = useState(''); // 富文本编辑器内容
  const [product, setProduct] = useState(null);
  const [pendingDeletionImages, setPendingDeletionImages] = useState([]); // 待删除的图片列表
  const [bulkPrice, setBulkPrice] = useState(null);
  const [bulkCurrency, setBulkCurrency] = useState(null);
  const [bulkWeight, setBulkWeight] = useState(null);
  const [descriptionFileList, setDescriptionFileList] = useState([]); // 商品描述图片列表
  const [pendingDeletionDescriptionImages, setPendingDeletionDescriptionImages] = useState([]); // 待删除的描述图片列表
  const [deleteVariantIds, setDeleteVariantIds] = useState([]); // 待删除的 variant ID 列表
  const [editingOption, setEditingOption] = useState(null); // { levelIndex, optionIndex, value }
  const [newOptionInputs, setNewOptionInputs] = useState({});
  const [invalidVariantIndexes, setInvalidVariantIndexes] = useState([]);
  const variantCardRefs = useRef([]);
  const addDeleteVariantIds = useCallback((variantsToRemove = []) => {
    setDeleteVariantIds(prev => {
      const idSet = new Set(prev);
      variantsToRemove.forEach(variant => {
        if (variant?.id) {
          idSet.add(variant.id);
        }
      });
      return Array.from(idSet);
    });
  }, [setDeleteVariantIds]);

  useEffect(() => {
    fetchCategories();
    fetchFreightChannels();
    
    if (isEditMode) {
      fetchProduct();
    } else {
      // 新建模式，初始化默认值
      setIsSingleProduct(true);
      setVariants([{ variant_path: [], SKU: '', price: 0, currency: 'USD', stock: null, weight: 100 }]);
      setVariantTypes([]);
      setVariantLevels([]);
      setVariantImages({});
      setExistingVariantImages({});
      setDescriptionContent('');
      setPendingDeletionImages([]);
      setDescriptionFileList([]);
      setPendingDeletionDescriptionImages([]);
      setBulkPrice(null);
      setBulkCurrency(null);
      setBulkWeight(null);
      setDeleteVariantIds([]);
    }
  }, [isEditMode, product_id]);

  useEffect(() => {
    if (isSingleProduct || variantLevels.length === 0) {
      setInvalidVariantIndexes([]);
    }
  }, [isSingleProduct, variantLevels.length]);

  useEffect(() => {
    variantCardRefs.current = variantCardRefs.current.slice(0, variants.length);
  }, [variants.length]);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      message.error('获取分类列表失败');
    }
  };

  const fetchFreightChannels = async () => {
    try {
      const response = await dropshipperAPI.getFreightChannels();
      setFreightChannels(response.data);
    } catch (error) {
      message.error('获取物流渠道失败');
    }
  };

  const fetchProduct = useCallback(async () => {
    if (!product_id) return;
    
    try {
      setLoading(true);
      const response = await productAPI.getById(product_id);
      const record = response.data;
      setProduct(record);
      
      // 设置variant_types
      setVariantTypes(record.variant_types || []);
      
      // 判断是否为单品
      const isSimple = record.variant_types === null || record.variant_types.length === 0;
      setIsSingleProduct(isSimple);
      
      if (isSimple) {
        // 单品
        let existingVariants;
        if (Array.isArray(record.variants) && record.variants.length > 0) {
          // 从数组中取第一个变体（保留 id）
          existingVariants = { ...record.variants[0] };
          delete existingVariants.variant_path; // 移除variant_path字段
        } else {
          // 默认值
          existingVariants = { SKU: '', price: 0, currency: 'USD', stock: -1, weight: 100 };
        }
        
        // 如果现有库存是-1，显示为null（空）
        if (existingVariants.stock === -1) {
          existingVariants.stock = null;
        }
        // 确保weight字段存在
        if (existingVariants.weight === undefined) {
          existingVariants.weight = 100;
        }
        
        setVariants([{ variant_path: [], ...existingVariants }]);
        setVariantLevels([]);
      } else {
        // 多规格 - 保留 variant id
        setVariants((record.variants || []).map(v => ({
          ...v,
          stock: v.stock === -1 ? null : v.stock
        })));
        
        // 设置规格层级
        const levels = (record.variant_types || []).map(type => ({
          name: type,
          values: getUniqueValuesForType(record.variants, record.variant_types, type)
        }));
        setVariantLevels(levels);
      }
      
      form.setFieldsValue({
        name: record.name,
        description: record.description,
        category_id: record.category_id,
        supported_freight_channels: record.supported_freight_channels || [],
        is_active: record.is_active
      });
      setDescriptionContent(record.description || '');
      
      // 设置现有图片到文件列表
      const existingImages = record.images?.map((img, index) => ({
        uid: `existing-${img.id}`,
        name: img.filename,
        status: 'done',
        url: `${API_BASE_URL}/products/${record.id}/images/${img.id}`,
        imageId: img.id,
        isExisting: true,
        forVariants: img.for_variants || false
      })) || [];
      
      setFileList(existingImages);
      
      // 设置现有的variant图片
      const variantImagesMap = {};
      if (Array.isArray(record.variants)) {
        record.variants.forEach((variant, index) => {
          if (variant.image) {
            variantImagesMap[index] = {
              id: variant.image,
              url: `${API_BASE_URL}/products/${record.id}/images/${variant.image}`,
              productId: record.id
            };
          }
        });
      }
      setExistingVariantImages(variantImagesMap);
      setVariantImages({}); // 清理新上传的variant图片
      setPendingDeletionImages([]); // 清空待删除列表
      
      // 设置现有的描述图片到文件列表
      const existingDescImages = record.description_images?.map((img) => ({
        uid: `existing-desc-${img.id}`,
        name: img.filename,
        status: 'done',
        url: `${API_BASE_URL}/products/${record.id}/description_images/${img.id}`,
        imageId: img.id,
        isExisting: true
      })) || [];
      
      setDescriptionFileList(existingDescImages);
      setPendingDeletionDescriptionImages([]);
      setDeleteVariantIds([]); // 清空待删除的 variant ID 列表
      
      setBulkPrice(null);
      setBulkCurrency(null);
      setBulkWeight(null);
    } catch (error) {
      message.error('获取商品信息失败');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  }, [product_id, form, navigate]);

  // 获取指定类型的所有唯一值
  const getUniqueValuesForType = (variants, variantTypes, typeName) => {
    if (!variants || !variantTypes || !Array.isArray(variants)) return [];
    
    const typeIndex = variantTypes.indexOf(typeName);
    if (typeIndex === -1) return [];
    
    const values = new Set();
    
    variants.forEach(variant => {
      if (variant.variant_path && variant.variant_path[typeIndex]) {
        values.add(variant.variant_path[typeIndex]);
      }
    });
    
    return Array.from(values);
  };

  // 重新构建variants数据结构 - 不再自动生成组合
  const rebuildVariants = (levels) => {
    if (levels.length === 0) {
      setVariants([]);
      setVariantTypes([]);
      return;
    }

    const newVariantTypes = levels.map(level => level.name);
    setVariantTypes(newVariantTypes);
    
    // 不自动生成variants，保持现有variants不变
    // 只更新variantTypes
  };
  
  const syncVariantPaths = (levelIndex, oldValue, newValue) => {
    if (oldValue === undefined) return;
    setVariants(prevVariants => (
      prevVariants.map(variant => {
        if (!variant.variant_path || variant.variant_path[levelIndex] !== oldValue) return variant;
        const newPath = [...variant.variant_path];
        newPath[levelIndex] = newValue === undefined ? undefined : newValue;
        return { ...variant, variant_path: newPath };
      })
    ));
  };

  const handleStartEditOption = (levelIndex, optionIndex, value) => {
    setEditingOption({ levelIndex, optionIndex, value });
  };

  const handleCancelOptionEdit = () => {
    setEditingOption(null);
  };

  const handleSaveOptionEdit = () => {
    if (!editingOption) return;
    const { levelIndex, optionIndex, value } = editingOption;
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) {
      message.warning('选项名称不能为空');
      return;
    }

    let shouldCloseEditor = false;
    setVariantLevels(prevLevels => {
      const targetLevel = prevLevels[levelIndex];
      if (!targetLevel) return prevLevels;

      const duplicate = targetLevel.values.some((item, idx) => idx !== optionIndex && item === trimmedValue);
      if (duplicate) {
        message.warning('已存在同名选项');
        return prevLevels;
      }

      const oldValue = targetLevel.values[optionIndex];
      if (oldValue === trimmedValue) {
        shouldCloseEditor = true;
        return prevLevels;
      }

      const newLevels = prevLevels.map((level, idx) => {
        if (idx !== levelIndex) return level;
        const newValues = [...level.values];
        newValues[optionIndex] = trimmedValue;
        return { ...level, values: newValues };
      });

      rebuildVariants(newLevels);
      syncVariantPaths(levelIndex, oldValue, trimmedValue);
      shouldCloseEditor = true;
      return newLevels;
    });

    if (shouldCloseEditor) {
      setEditingOption(null);
    }
  };

  // 计算删除选项时受影响的SKU
  const getAffectedSkus = (levelIndex, optionValue) => {
    return variants.filter(variant =>
      variant.variant_path &&
      variant.variant_path[levelIndex] === optionValue
    ).map(variant => ({
      sku: variant.SKU || `SKU ${variants.indexOf(variant) + 1}`,
      variant_path: variant.variant_path.filter(Boolean).join(' / ') || '默认规格',
      index: variants.indexOf(variant)
    }));
  };

  const handleRemoveOption = (levelIndex, optionIndex) => {
    const targetLevel = variantLevels[levelIndex];
    if (!targetLevel) return;

    const removedValue = targetLevel.values[optionIndex];

    // 首先确认是否删除选项
    if (!window.confirm(`确定要删除选项"${removedValue}"吗？`)) {
      return; // 用户取消删除选项
    }

    const affectedSkus = getAffectedSkus(levelIndex, removedValue);

    // 删除选项
    setVariantLevels(prevLevels => {
      const newValues = targetLevel.values.filter((_, idx) => idx !== optionIndex);
      const newLevels = prevLevels.map((level, idx) => (
        idx === levelIndex ? { ...level, values: newValues } : level
      ));
      rebuildVariants(newLevels);
      syncVariantPaths(levelIndex, removedValue, undefined);
      return newLevels;
    });

    if (editingOption && editingOption.levelIndex === levelIndex) {
      setEditingOption(null);
    }

    // 如果有受影响的SKU，询问是否同时删除
    if (affectedSkus.length > 0) {
      const skuDetails = affectedSkus.map(sku =>
        `SKU: 【${sku.sku}】, 规格: 【${sku.variant_path}】`
      ).join('\n');

      const confirmMessage = `选项"${removedValue}"已删除。是否同时删除关联该选项的 ${affectedSkus.length} 个SKU?\n\n${skuDetails}\n\n点击确定则删除，点击取消则保留这些SKU\n若保留，稍后请手动重新设置这些SKU的规格`;

      if (window.confirm(confirmMessage)) {
        const variantsToRemove = affectedSkus
          .map(sku => variants[sku.index])
          .filter(Boolean);
        addDeleteVariantIds(variantsToRemove);
        // 用户确认删除SKU，从variants中移除这些SKU
        setVariants(prevVariants =>
          prevVariants.filter((_, index) =>
            !affectedSkus.some(sku => sku.index === index)
          )
        );
      }
    }
  };

  const handleAddOption = (levelIndex) => {
    const value = (newOptionInputs[levelIndex] || '').trim();
    if (!value) {
      message.warning('请输入选项名称');
      return;
    }

    let added = false;
    setVariantLevels(prevLevels => {
      const targetLevel = prevLevels[levelIndex];
      if (!targetLevel) return prevLevels;
      if (targetLevel.values.includes(value)) {
        message.warning('已存在同名选项');
        return prevLevels;
      }
      const newLevels = prevLevels.map((level, idx) => {
        if (idx !== levelIndex) return level;
        return { ...level, values: [...level.values, value] };
      });
      added = true;
      rebuildVariants(newLevels);
      return newLevels;
    });

    if (added) {
      setNewOptionInputs(prev => ({ ...prev, [levelIndex]: '' }));
    }
  };

  const renderVariantOptionEditor = (level, levelIndex) => {
    return (
      <>
        <Space size={[8, 8]} wrap>
          {level.values.map((value, optionIndex) => {
            const isEditing = editingOption 
              && editingOption.levelIndex === levelIndex 
              && editingOption.optionIndex === optionIndex;
            if (isEditing) {
              return (
                <Space key={`${value}-${optionIndex}`} size="small" align="center">
                  <Input
                    size="small"
                    style={{ width: 160 }}
                    value={editingOption.value}
                    onChange={(e) => setEditingOption(prev => ({ ...prev, value: e.target.value }))}
                    onPressEnter={handleSaveOptionEdit}
                    autoFocus
                  />
                  <Button size="small" type="primary" onClick={handleSaveOptionEdit}>
                    保存
                  </Button>
                  <Button size="small" onClick={handleCancelOptionEdit}>
                    取消
                  </Button>
                </Space>
              );
            }

            return (
              <Tag
                key={`${value}-${optionIndex}`}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  handleRemoveOption(levelIndex, optionIndex);
                }}
                style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <span>{value}</span>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleStartEditOption(levelIndex, optionIndex, value)}
                />
              </Tag>
            );
          })}
        </Space>
        <Space style={{ marginTop: 8 }} align="center">
          <Input
            style={{ width: 200 }}
            placeholder="输入选项名称"
            value={newOptionInputs[levelIndex] || ''}
            onChange={(e) => setNewOptionInputs(prev => ({ ...prev, [levelIndex]: e.target.value }))}
            onPressEnter={() => handleAddOption(levelIndex)}
          />
          <Button type="dashed" icon={<PlusOutlined />} onClick={() => handleAddOption(levelIndex)}>
            添加选项
          </Button>
        </Space>
      </>
    );
  };

  const getVariantPathIssues = (variantData) => {
    if (isSingleProduct || variantLevels.length === 0) return [];
    const issues = [];
    if (!variantData || !Array.isArray(variantData.variant_path)) {
      issues.push('未选择任何规格');
      return issues;
    }
    variantLevels.forEach((level, levelIndex) => {
      const value = variantData.variant_path[levelIndex];
      if (!value) {
        issues.push(`规格「${level.name}」未选择`);
        return;
      }
      if (!Array.isArray(level?.values) || level.values.length === 0 || !level.values.includes(value)) {
        issues.push(`规格「${level.name}」选项无效，请重新选择`);
      }
    });
    return issues;
  };

  const isVariantPathValid = (variantData) => getVariantPathIssues(variantData).length === 0;

  const getVariantDisplayLabel = (variantData, index) => {
    const skuPart = variantData?.SKU?.trim() ? variantData.SKU.trim() : `SKU ${index + 1}`;
    const pathPart = Array.isArray(variantData?.variant_path)
      ? variantData.variant_path.filter(Boolean).join(' / ')
      : '';
    return pathPart ? `${skuPart} (${pathPart})` : skuPart;
  };

  const validateVariantPaths = () => {
    if (isSingleProduct || variantLevels.length === 0) {
      setInvalidVariantIndexes([]);
      return true;
    }
    const invalidIndexes = [];
    const detailedErrors = [];
    variants.forEach((variant, index) => {
      const issues = getVariantPathIssues(variant);
      if (issues.length > 0) {
        invalidIndexes.push(index);
        detailedErrors.push({
          label: getVariantDisplayLabel(variant, index),
          issues
        });
      }
    });
    setInvalidVariantIndexes(invalidIndexes);
    if (invalidIndexes.length > 0) {
      message.error('存在未正确配置的SKU规格，请检查标红的卡片');
      const alertMessage = detailedErrors
        .map(({ label, issues }) => `${label}:\n${issues.map(issue => ` - ${issue}`).join('\n')}`)
        .join('\n\n');
      window.alert(`以下SKU规格存在问题：\n\n${alertMessage}`);
      scrollToVariantCard(invalidIndexes[0]);
      return false;
    }
    return true;
  };

  const scrollToVariantCard = (index) => {
    const targetNode = variantCardRefs.current[index];
    if (targetNode && typeof targetNode.scrollIntoView === 'function') {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleVariantPathChange = (variantIndex, levelIndex, value) => {
    const newVariants = [...variants];
    if (!newVariants[variantIndex].variant_path) {
      newVariants[variantIndex].variant_path = new Array(variantLevels.length).fill('');
    }
    newVariants[variantIndex].variant_path[levelIndex] = value;
    setVariants(newVariants);
    const variantIsValid = isVariantPathValid(newVariants[variantIndex]);
    setInvalidVariantIndexes(prev => {
      if (variantIsValid) {
        return prev.filter(idx => idx !== variantIndex);
      }
      return prev;
    });
  };

  // 添加新的SKU
  const handleAddSKU = () => {
    if (variantLevels.length === 0) {
      message.warning('请先设置规格层级');
      return;
    }
    
    // 检查是否所有规格层级都有值
    const hasEmptyLevels = variantLevels.some(level => !level.values || level.values.length === 0);
    if (hasEmptyLevels) {
      message.warning('请先为所有规格层级添加选项');
      return;
    }
    
    // 创建新的空variant
    const newVariant = {
      variant_path: [],
      SKU: '',
      price: 0,
      currency: 'USD',
      stock: null,
      weight: 100,
      image: null
    };
    
    setVariants([...variants, newVariant]);
    message.success('SKU添加成功');
  };

  // 渲染单个SKU的规格选择器
  const renderSKUVariantSelectors = (variantIndex) => {
    if (variantLevels.length === 0) return null;
    
    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {variantLevels.map((level, levelIndex) => (
          <Col span={24 / variantLevels.length} key={levelIndex}>
            <Form.Item label={level.name} required>
              <Select
                placeholder={`选择${level.name}`}
                style={{ width: '100%' }}
                value={variants[variantIndex]?.variant_path?.[levelIndex] || undefined}
                onChange={(value) => handleVariantPathChange(variantIndex, levelIndex, value)}
              >
                {level.values.map(value => (
                  <Option key={value} value={value}>{value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        ))}
      </Row>
    );
  };

  const applyBulkPrice = () => {
    if (bulkPrice === null || bulkPrice === undefined || Number.isNaN(bulkPrice)) {
      message.warning('请先输入要应用的价格');
      return;
    }
    setVariants(prev => prev.map(variant => ({
      ...variant,
      price: bulkPrice
    })));
    message.success('已应用统一价格');
  };

  const applyBulkCurrency = () => {
    if (!bulkCurrency) {
      message.warning('请先选择要应用的货币类型');
      return;
    }
    setVariants(prev => prev.map(variant => ({
      ...variant,
      currency: bulkCurrency
    })));
    message.success('已应用统一货币类型');
  };

  const applyBulkWeight = () => {
    if (bulkWeight === null || bulkWeight === undefined || Number.isNaN(bulkWeight)) {
      message.warning('请先输入要应用的重量');
      return;
    }
    setVariants(prev => prev.map(variant => ({
      ...variant,
      weight: bulkWeight
    })));
    message.success('已应用统一重量');
  };

  // 渲染规格详情设置
  const renderVariantDetails = () => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return null;
    }

    return (
      <Card title="已添加的SKU" size="small" style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Space align="center" size="middle" wrap>
            <Text strong>统一设置价格</Text>
            <InputNumber
              value={bulkPrice}
              onChange={setBulkPrice}
              min={0}
              precision={2}
              placeholder="价格"
              style={{ width: 160 }}
            />
            <Button type="primary" onClick={applyBulkPrice}>
              应用到下方所有SKU价格
            </Button>
          </Space>
          <Space align="center" size="middle" wrap style={{ marginTop: 8 }}>
            <Text strong>统一设置货币</Text>
            <Select
              value={bulkCurrency}
              onChange={setBulkCurrency}
              placeholder="货币类型"
              style={{ width: 160 }}
            >
              <Option value="USD">USD</Option>
              <Option value="CNY">CNY</Option>
            </Select>
            <Button type="primary" onClick={applyBulkCurrency}>
              应用到下方所有SKU货币
            </Button>
          </Space>
          <Space align="center" size="middle" wrap style={{ marginTop: 8 }}>
            <Text strong>统一设置重量</Text>
            <InputNumber
              value={bulkWeight}
              onChange={setBulkWeight}
              min={1}
              precision={0}
              placeholder="重量（克）"
              style={{ width: 160 }}
            />
            <Button type="primary" onClick={applyBulkWeight}>
              应用到下方所有SKU重量
            </Button>
          </Space>
        </div>
        {variants.map((variant, index) => {
          const isVariantInvalid = invalidVariantIndexes.includes(index);
          return (
            <div
              key={index}
              ref={(el) => {
                variantCardRefs.current[index] = el;
              }}
            >
              <Card 
              title={`SKU ${index + 1}: ${variant.SKU || ''} ${variant.variant_path && variant.variant_path.length > 0 && variant.variant_path.every(v => v) ? `(${variant.variant_path.join(' - ')})` : ''}`}
              size="small"
              style={{ 
                marginBottom: 12,
                border: isVariantInvalid ? '1px solid #ff4d4f' : undefined
              }}
              headStyle={isVariantInvalid ? { color: '#ff4d4f' } : undefined}
              extra={
                <Button 
                  type="link" 
                  danger 
                  size="small"
                  onClick={() => {
                    const variantToDelete = variants[index];
                    
                    // 如果这个 variant 有 id（是现有的），添加到待删除列表
                    addDeleteVariantIds([variantToDelete]);
                    
                    const newVariants = variants.filter((_, i) => i !== index);
                    setVariants(newVariants);
                    setInvalidVariantIndexes(prev => prev
                      .filter(idx => idx !== index)
                      .map(idx => (idx > index ? idx - 1 : idx))
                    );
                    
                    // 如果有现有的variant图片，添加到待删除列表
                    if (existingVariantImages[index]) {
                      setPendingDeletionImages(prev => [...prev, existingVariantImages[index]]);
                    }
                    
                    // 清理对应的新上传图片
                    setVariantImages(prev => {
                      const newImages = { ...prev };
                      delete newImages[index];
                      // 重新索引
                      const reindexedImages = {};
                      Object.keys(newImages).forEach(key => {
                        const keyIndex = parseInt(key);
                        if (keyIndex > index) {
                          reindexedImages[keyIndex - 1] = newImages[key];
                        } else if (keyIndex < index) {
                          reindexedImages[keyIndex] = newImages[key];
                        }
                      });
                      return reindexedImages;
                    });
                    
                    // 清理对应的现有图片并重新索引
                    setExistingVariantImages(prev => {
                      const newImages = { ...prev };
                      delete newImages[index];
                      // 重新索引
                      const reindexedImages = {};
                      Object.keys(newImages).forEach(key => {
                        const keyIndex = parseInt(key);
                        if (keyIndex > index) {
                          reindexedImages[keyIndex - 1] = newImages[key];
                        } else if (keyIndex < index) {
                          reindexedImages[keyIndex] = newImages[key];
                        }
                      });
                      return reindexedImages;
                    });
                  }}
                >
                  删除
                </Button>
              }
              >
              {/* 规格选择器 */}
              {renderSKUVariantSelectors(index)}
              
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="SKU" required>
                    <Input 
                      value={variant.SKU || ''}
                      onChange={(e) => updateVariantData(index, 'SKU', e.target.value)}
                      placeholder="请输入SKU"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="价格" required>
                    <InputNumber 
                      value={variant.price || 0}
                      onChange={(value) => updateVariantData(index, 'price', value || 0)}
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      placeholder="请输入价格"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="货币类型" required>
                    <Select 
                      value={variant.currency || 'USD'}
                      onChange={(value) => updateVariantData(index, 'currency', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="USD">USD</Option>
                      <Option value="CNY">CNY</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="库存">
                    <InputNumber 
                      value={variant.stock === -1 ? null : variant.stock}
                      onChange={(value) => updateVariantData(index, 'stock', value)}
                      style={{ width: '100%' }}
                      min={0}
                      placeholder="留空表示无限"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="重量 (克)" required>
                    <InputNumber 
                      value={variant.weight || 100}
                      onChange={(value) => updateVariantData(index, 'weight', value || 100)}
                      style={{ width: '100%' }}
                      min={1}
                      precision={0}
                      placeholder="请输入重量（克）"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="图片">
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      {/* 显示现有的variant图片 */}
                      {existingVariantImages[index] && !pendingDeletionImages.some(img => img.id === existingVariantImages[index].id) && (
                        <div style={{ 
                          border: '1px solid #d9d9d9', 
                          borderRadius: '8px', 
                          padding: '4px',
                          position: 'relative'
                        }}>
                          <Image
                            src={existingVariantImages[index].url}
                            width={100}
                            height={100}
                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                            preview={false}
                          />
                          <Button
                            size="small"
                            danger
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#fff',
                              border: '1px solid #ff4d4f'
                            }}
                            onClick={() => {
                              // 添加到待删除列表
                              setPendingDeletionImages(prev => [...prev, existingVariantImages[index]]);
                              // 清除现有图片引用
                              setExistingVariantImages(prev => {
                                const newImages = { ...prev };
                                delete newImages[index];
                                return newImages;
                              });
                              // 直接将variants数组中的image字段置为null
                              setVariants(prev => {
                                const newVariants = [...prev];
                                if (newVariants[index]) {
                                  newVariants[index] = {
                                    ...newVariants[index],
                                    image: null
                                  };
                                }
                                return newVariants;
                              });
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      )}
                      
                      {/* 上传图片按钮 - 只在没有现有图片或现有图片已删除时显示 */}
                      {(!existingVariantImages[index] || pendingDeletionImages.some(img => img.id === existingVariantImages[index].id)) && (
                        <Upload
                          listType="picture-card"
                          fileList={getVariantFileList(index)}
                          beforeUpload={(file) => handleVariantImageUpload(file, index)}
                          onRemove={() => handleRemoveVariantImage(index)}
                          maxCount={1}
                          accept="image/*"
                          style={{ marginBottom: 0 }}
                        >
                          {getVariantFileList(index).length >= 1 ? null : (
                            <div>
                              <PlusOutlined />
                              <div style={{ marginTop: 8 }}>上传图片</div>
                            </div>
                          )}
                        </Upload>
                      )}
                    </div>
                  </Form.Item>
                </Col>
              </Row>
              </Card>
            </div>
          );
        })}
      </Card>
    );
  };

  // 更新规格数据
  const updateVariantData = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index] = {
      ...newVariants[index],
      [field]: value
    };
    setVariants(newVariants);
  };

  // 获取变体图片文件列表
  const getVariantFileList = (variantIndex) => {
    const variantImage = variantImages[variantIndex];
    if (!variantImage) return [];
    
    return [{
      uid: `variant-${variantIndex}`,
      name: variantImage.name,
      status: 'done',
      url: URL.createObjectURL(variantImage),
      originFileObj: variantImage
    }];
  };

  // 处理变体图片上传
  const handleVariantImageUpload = async (file, variantIndex) => {
    try {
      // 检查文件类型
      if (!isImageFile(file)) {
        message.error('只能上传图片文件');
        return false;
      }

      // 压缩图片
      const compressedFile = await compressImage(file);
      
      // 更新状态
      setVariantImages(prev => ({
        ...prev,
        [variantIndex]: compressedFile
      }));

      message.success('图片上传成功');
    } catch (error) {
      
      message.error('图片上传失败');
    }
    
    return false; // 阻止自动上传
  };

  // 删除变体图片
  const handleRemoveVariantImage = (variantIndex) => {
    setVariantImages(prev => {
      const newImages = { ...prev };
      delete newImages[variantIndex];
      return newImages;
    });
  };

  // 处理提交时的variants数据，将null的stock转换为-1
  const processVariantsForSubmit = (variantsData) => {
    if (!Array.isArray(variantsData)) return [];
    
    return variantsData.map(variant => ({
      ...variant,
      stock: variant.stock === null || variant.stock === undefined ? -1 : variant.stock
    }));
  };

  const handleSubmit = async (values) => {
    if (!validateVariantPaths()) {
      return;
    }
    try {
      // 首先删除待删除的图片
      if (pendingDeletionImages.length > 0 && isEditMode) {
        for (const image of pendingDeletionImages) {
          try {
            await productAPI.deleteImage(image.productId || product_id, image.id);
            
          } catch (error) {
            
            message.warning(`删除部分图片失败，但会继续保存`);
          }
        }
      }
      
      // 删除待删除的描述图片
      if (pendingDeletionDescriptionImages.length > 0 && isEditMode) {
        for (const image of pendingDeletionDescriptionImages) {
          try {
            await productAPI.deleteDescriptionImage(image.productId || product_id, image.id);
            
          } catch (error) {
            
            message.warning(`删除部分描述图片失败，但会继续保存`);
          }
        }
      }
      
      const formData = new FormData();
      
      // 添加表单数据
      formData.append('name', values.name);
      formData.append('description', descriptionContent || '');
      formData.append('category_id', values.category_id);
      formData.append('is_active', values.is_active);
      
      // 处理variants数据，将null的stock转换为-1（保留 id 字段）
      let processedVariants;
      if (isSingleProduct) {
        // 单品：提交包含单个variant的数组（保留 id）
        const singleVariant = variants[0] || {};
        const { variant_path, ...variantData } = singleVariant; // 移除variant_path字段
        processedVariants = [{
          ...variantData,
          stock: variantData.stock === null || variantData.stock === undefined ? -1 : variantData.stock
        }];
      } else {
        // 多规格：提交variants数组（保留 id）
        processedVariants = processVariantsForSubmit(variants);
      }
      formData.append('variants', JSON.stringify(processedVariants));
      
      // 添加待删除的 variant IDs
      if (isEditMode && deleteVariantIds.length > 0) {
        formData.append('delete_variant_ids', JSON.stringify(deleteVariantIds));
      }
      
      // 添加variant_types（仅在多规格时）
      if (!isSingleProduct && variantTypes.length > 0) {
        formData.append('variant_types', JSON.stringify(variantTypes));
      }
      
      // 添加支持的物流渠道
      formData.append('supported_freight_channels', JSON.stringify(values.supported_freight_channels || []));
      
      // 处理新上传的图片文件（压缩）
      const newFiles = fileList.filter(file => file.originFileObj && !file.isExisting);
      console.log('Uploading files:', newFiles.map(f => ({ name: f.name, size: f.size })));
      
      // 压缩图片
      const processedFiles = [];
      for (const file of newFiles) {
        if (isImageFile(file.originFileObj)) {
          // 图片文件：检查大小并压缩
          if (file.originFileObj.size > 1024 * 1024) { // 超过1MB
            message.loading(`正在压缩图片 ${file.name}...`, 0);
            try {
              const compressedFile = await compressImage(file.originFileObj, 700); // 压缩到700KB
              processedFiles.push(compressedFile);
              message.destroy();
              const formatInfo = file.originFileObj.type !== 'image/jpeg' && file.originFileObj.type !== 'image/jpg' 
                ? ` (已转换为JPG格式)` 
                : '';
              message.success(`图片 ${file.name} 已压缩 (${formatFileSize(file.originFileObj.size)} → ${formatFileSize(compressedFile.size)})${formatInfo}`);
            } catch (error) {
              message.destroy();
              message.warning(`图片 ${file.name} 压缩失败，使用原文件`);
              processedFiles.push(file.originFileObj);
            }
          } else {
            processedFiles.push(file.originFileObj);
          }
        } else {
          processedFiles.push(file.originFileObj);
        }
      }
      
      // 添加处理后的图片文件
      processedFiles.forEach(file => {
        formData.append('images', file);
      });

      // 处理variant图片 - 添加二次检查和压缩
      const variantImageFiles = [];
      const variantImageIndices = []; // 记录每个variant图片对应的variant索引
      
      for (let i = 0; i < variants.length; i++) {
        const variantImage = variantImages[i];
        if (variantImage) {
          // 检查variant图片大小并压缩（和主图片相同的逻辑）
          if (isImageFile(variantImage) && variantImage.size > 1024 * 1024) { // 超过1MB
            message.loading(`正在压缩变体图片...`, 0);
            try {
              const compressedFile = await compressImage(variantImage, 700); // 压缩到700KB
              variantImageFiles.push(compressedFile);
              variantImageIndices.push(i);
              message.destroy();
              const formatInfo = variantImage.type !== 'image/jpeg' && variantImage.type !== 'image/jpg' 
                ? ` (已转换为JPG格式)` 
                : '';
              message.success(`变体图片已压缩 (${formatFileSize(variantImage.size)} → ${formatFileSize(compressedFile.size)})${formatInfo}`);
            } catch (error) {
              message.destroy();
              message.warning(`变体图片压缩失败，使用原文件`);
              variantImageFiles.push(variantImage);
              variantImageIndices.push(i);
            }
          } else {
            variantImageFiles.push(variantImage);
            variantImageIndices.push(i);
          }
        }
      }
      
      // 添加处理后的variant图片文件
      variantImageFiles.forEach((file) => {
        if (file) {
          formData.append('images', file);
        }
      });
      
      // 创建variant图片标记（主图片标记为false，variant图片标记为对应的variant索引）
      const allImageFlags = [
        ...new Array(processedFiles.length).fill(false), // 主图片标记为false
        ...variantImageIndices // variant图片标记为对应的variant索引
      ];
      formData.append('variant_image_flags', JSON.stringify(allImageFlags));
      
      // 如果是编辑模式，设置是否保留现有图片
      if (isEditMode) {
        const hasExistingImages = fileList.some(file => file.isExisting);
        formData.append('keep_existing_images', hasExistingImages);
      }

      // 处理描述图片上传
      const newDescFiles = descriptionFileList.filter(file => file.originFileObj && !file.isExisting);
      console.log('Uploading description files:', newDescFiles.map(f => ({ name: f.name, size: f.size })));
      
      // 压缩描述图片
      const processedDescFiles = [];
      for (const file of newDescFiles) {
        if (isImageFile(file.originFileObj)) {
          // 图片文件：检查大小并压缩
          if (file.originFileObj.size > 1024 * 1024) { // 超过1MB
            message.loading(`正在压缩描述图片 ${file.name}...`, 0);
            try {
              const compressedFile = await compressImage(file.originFileObj, 700); // 压缩到700KB
              processedDescFiles.push(compressedFile);
              message.destroy();
              const formatInfo = file.originFileObj.type !== 'image/jpeg' && file.originFileObj.type !== 'image/jpg' 
                ? ` (已转换为JPG格式)` 
                : '';
              message.success(`描述图片 ${file.name} 已压缩 (${formatFileSize(file.originFileObj.size)} → ${formatFileSize(compressedFile.size)})${formatInfo}`);
            } catch (error) {
              message.destroy();
              message.warning(`描述图片 ${file.name} 压缩失败，使用原文件`);
              processedDescFiles.push(file.originFileObj);
            }
          } else {
            processedDescFiles.push(file.originFileObj);
          }
        } else {
          processedDescFiles.push(file.originFileObj);
        }
      }
      
      // 添加处理后的描述图片文件
      processedDescFiles.forEach(file => {
        formData.append('description_images', file);
      });
      
      // 如果是编辑模式，设置是否保留现有描述图片
      if (isEditMode) {
        const hasExistingDescImages = descriptionFileList.some(file => file.isExisting);
        formData.append('keep_existing_description_images', hasExistingDescImages);
      }

      if (isEditMode) {
        await productAPI.update(product_id, formData);
        message.success('更新成功');
      } else {
        await productAPI.create(formData);
        message.success('创建成功');
      }
      
      navigate('/admin/products');
    } catch (error) {
      
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      message.error(`${isEditMode ? '更新' : '创建'}失败: ${errorMessage}`);
    }
  };

  // 图片上传前的验证
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    
    // 图片文件限制1MB（会自动压缩）
    if (file.size > 1024 * 1024) {
      message.warning(`图片 ${file.name} 超过1MB，将自动压缩并转换为JPG格式上传`);
    }
    
    // 检查总图片数量（包括现有的）
    // 移除图片数量限制
    
    return false; // 阻止自动上传，我们手动处理
  };

  // 文件列表变化处理
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // 预览图片
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  // 删除现有图片（添加到待删除列表，不立即删除）
  const handleRemoveExistingImage = async (file) => {
    if (file.isExisting && isEditMode) {
      // 添加到待删除列表而不是立即删除
      setPendingDeletionImages(prev => [...prev, { 
        id: file.imageId, 
        productId: product_id 
      }]);
      // 从文件列表中移除
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
      return true;
    }
    return true; // 允许删除新上传的文件
  };

  // 描述图片上传前的验证
  const beforeDescriptionUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    
    // 图片文件限制1MB（会自动压缩）
    if (file.size > 1024 * 1024) {
      message.warning(`图片 ${file.name} 超过1MB，将自动压缩并转换为JPG格式上传`);
    }
    
    return false; // 阻止自动上传，我们手动处理
  };

  // 描述图片文件列表变化处理
  const handleDescriptionFileChange = ({ fileList: newFileList }) => {
    setDescriptionFileList(newFileList);
  };

  // 删除现有描述图片（添加到待删除列表，不立即删除）
  const handleRemoveExistingDescriptionImage = async (file) => {
    if (file.isExisting && isEditMode) {
      // 添加到待删除列表而不是立即删除
      setPendingDeletionDescriptionImages(prev => [...prev, { 
        id: file.imageId, 
        productId: product_id 
      }]);
      // 从文件列表中移除
      setDescriptionFileList(prev => prev.filter(item => item.uid !== file.uid));
      return true;
    }
    return true; // 允许删除新上传的文件
  };

  // 设置封面图片
  const handleSetCover = async (productId, imageId) => {
    try {
      await productAPI.setCoverImage(productId, imageId);
      message.success('封面图片设置成功');
      // 刷新产品信息
      if (isEditMode) {
        fetchProduct();
      }
    } catch (error) {
      message.error('设置封面图片失败');
    }
  };

  // 获取图片base64用于预览
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面头部 */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/admin/products')}
          style={{ marginRight: 16 }}
        >
          返回商品列表
        </Button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
          {isEditMode ? '编辑商品' : '添加商品'}
        </h1>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px' }}
      >
        <Row gutter={24}>
          {/* 左列：基本信息 */}
          <Col span={12}>
            <Form.Item
              label="商品名称"
              name="name"
              rules={[
                { required: true, message: '请输入商品名称' },
              ]}
            >
              <Input placeholder="请输入商品名称" />
            </Form.Item>

            <Form.Item
              label="商品描述"
              name="description"
            >
              <SunEditor
                setContents={descriptionContent}
                onChange={setDescriptionContent}
                setOptions={{
                  height: 400,
                  buttonList: [
                    ['undo', 'redo'],
                    ['fontSize', 'formatBlock'],
                    ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
                    ['fontColor', 'hiliteColor', 'textStyle'],
                    ['removeFormat'],
                    ['outdent', 'indent'],
                    ['align', 'horizontalRule', 'list', 'lineHeight'],
                    ['table', 'link'],
                    ['fullScreen', 'showBlocks', 'codeView'],
                    ['preview']
                  ],
                  placeholder: '请输入商品描述...'
                }}
              />
            </Form.Item>

            <Form.Item
              label="商品描述图片"
              name="description_images"
              extra="用于在商品详情页面展示的描述图片"
            >
              <Upload
                listType="picture-card"
                fileList={descriptionFileList}
                beforeUpload={beforeDescriptionUpload}
                onChange={handleDescriptionFileChange}
                onPreview={handlePreview}
                onRemove={handleRemoveExistingDescriptionImage}
                multiple
                accept="image/*"
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传图片</div>
                </div>
              </Upload>
            </Form.Item>

            <Form.Item
              label="商品分类"
              name="category_id"
              rules={[
                { required: true, message: '请选择商品分类' },
              ]}
            >
              <Select 
                placeholder="请选择商品分类"
                showSearch
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {categories.map(category => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="商品图片"
              name="images"
              extra="支持多图上传，点击星号图标设置封面图片"
            >
              <Upload
                listType="picture-card"
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleFileChange}
                onPreview={handlePreview}
                onRemove={handleRemoveExistingImage}
                multiple
                accept="image/*"
                itemRender={(originNode, file, currFileList) => {
                  const isCover = isEditMode && file.imageId && product?.cover_picture === file.imageId;
                  return (
                    <div style={{ position: 'relative' }}>
                      {originNode}
                      {file.status === 'done' && file.isExisting && (
                        <Button
                          type={isCover ? "primary" : "default"}
                          size="small"
                          icon={isCover ? <StarFilled /> : <StarOutlined />}
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            zIndex: 1,
                            minWidth: 'auto',
                            width: 24,
                            height: 24,
                            padding: 0
                          }}
                          onClick={() => handleSetCover(product_id, file.imageId)}
                          title={isCover ? "当前封面图片" : "设置为封面图片"}
                        />
                      )}
                    </div>
                  );
                }}
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传图片</div>
                </div>
              </Upload>
            </Form.Item>

            <Form.Item
              label="支持的物流渠道"
              name="supported_freight_channels"
              extra="选择此商品支持的物流渠道，dropshipper只能选择商品支持的渠道"
            >
              <Select
                mode="multiple"
                placeholder="选择支持的物流渠道"
                style={{ width: '100%' }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {freightChannels.map(channel => (
                  <Option key={channel.id} value={channel.id}>
                    {channel.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="是否启用"
              name="is_active"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          </Col>

          {/* 右列：商品类型和规格管理 */}
          <Col span={12}>
            {/* 商品类型选择 */}
            <Form.Item label="商品类型">
              <Checkbox 
                checked={isSingleProduct} 
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsSingleProduct(checked);
                  if (checked) {
                    if (isEditMode && variants.length > 0) {
                      addDeleteVariantIds(variants);
                    }
                    const baseVariant = variants[0] ? { ...variants[0] } : { SKU: '', price: 0, currency: 'USD', stock: null, weight: 100 };
                    const { variant_path, id, ...variantRest } = baseVariant;
                    setVariants([{ variant_path: [], ...variantRest }]);
                    setVariantTypes([]);
                    setVariantLevels([]);
                  } else {
                    if (isEditMode && variants.length > 0) {
                      addDeleteVariantIds(variants);
                    }
                    setVariants([]);
                    setVariantTypes([]);
                    setVariantLevels([]);
                  }
                  setVariantImages({}); // 清理variant图片
                  setExistingVariantImages({}); // 清理现有variant图片
                }}
              >
                单品
              </Checkbox>
            </Form.Item>

            {isSingleProduct ? (
              // 单品表单
              <Card title="单品信息" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="SKU" required>
                      <Input 
                        value={variants[0]?.SKU || ''}
                        onChange={(e) => {
                          const newVariants = [...variants];
                          newVariants[0] = { ...newVariants[0], SKU: e.target.value };
                          setVariants(newVariants);
                        }}
                        placeholder="请输入SKU"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="货币类型" required>
                      <Select 
                        value={variants[0]?.currency || 'USD'}
                        onChange={(value) => {
                          const newVariants = [...variants];
                          newVariants[0] = { ...newVariants[0], currency: value };
                          setVariants(newVariants);
                        }}
                        style={{ width: '100%' }}
                      >
                        <Option value="USD">USD</Option>
                        <Option value="CNY">CNY</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="价格" required>
                      <InputNumber 
                        value={variants[0]?.price || 0}
                        onChange={(value) => {
                          const newVariants = [...variants];
                          newVariants[0] = { ...newVariants[0], price: value || 0 };
                          setVariants(newVariants);
                        }}
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        placeholder="请输入价格"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="库存（留空表示无限）">
                      <InputNumber 
                        value={variants[0]?.stock}
                        onChange={(value) => {
                          const newVariants = [...variants];
                          newVariants[0] = { ...newVariants[0], stock: value };
                          setVariants(newVariants);
                        }}
                        style={{ width: '100%' }}
                        min={0}
                        placeholder="留空表示无限库存"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="重量 (克)" required>
                      <InputNumber 
                        value={variants[0]?.weight || 100}
                        onChange={(value) => {
                          const newVariants = [...variants];
                          newVariants[0] = { ...newVariants[0], weight: value || 100 };
                          setVariants(newVariants);
                        }}
                        style={{ width: '100%' }}
                        min={1}
                        precision={0}
                        placeholder="请输入重量（克）"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ) : (
              // 多规格表单
              <Card title="规格管理" size="small" style={{ marginBottom: 16 }}>
                <Text type="secondary">支持多级规格，如颜色+尺寸+材质等</Text>
                
                {/* 规格层级设置 */}
                {variantLevels.map((level, levelIndex) => (
                  <Card 
                    key={levelIndex}
                    title={`第${levelIndex + 1}级规格: ${level.name}`}
                    size="small"
                    style={{ marginTop: 16 }}
                    extra={
                      <Button 
                        type="link" 
                        danger 
                        icon={<MinusCircleOutlined />}
                        onClick={() => {
                          const newLevels = variantLevels.filter((_, i) => i !== levelIndex);
                          setVariantLevels(newLevels);
                          // 重新构建variants
                          rebuildVariants(newLevels);
                        }}
                      >
                        删除
                      </Button>
                    }
                  >
                    <Form.Item label="名称">
                      <Input 
                        value={level.name}
                        onChange={(e) => {
                          const newLevels = [...variantLevels];
                          newLevels[levelIndex].name = e.target.value;
                          setVariantLevels(newLevels);
                          rebuildVariants(newLevels);
                        }}
                        placeholder={`如：${levelIndex === 0 ? 'Color' : levelIndex === 1 ? 'Size' : levelIndex === 2 ? 'Material' : `Level${levelIndex + 1}`}`}
                      />
                    </Form.Item>
                    <Form.Item label="选项">
                      {renderVariantOptionEditor(level, levelIndex)}
                    </Form.Item>
                  </Card>
                ))}

                <Button 
                  type="dashed" 
                  onClick={() => {
                    const levelNames = ['Color', 'Size', 'Material', 'Style', 'Flavor', 'Weight'];
                    const newLevel = {
                      name: levelNames[variantLevels.length] || `Level${variantLevels.length + 1}`,
                      values: []
                    };
                    setVariantLevels([...variantLevels, newLevel]);
                  }}
                  style={{ width: '100%', marginTop: 16 }}
                >
                  添加规格层级
                </Button>

                {/* 规格详情设置 */}
                {renderVariantDetails()}
                
                {/* 添加SKU按钮 */}
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Button 
                    type="dashed" 
                    onClick={handleAddSKU}
                    style={{ width: '100%' }}
                  >
                    添加SKU
                  </Button>
                </div>
              </Card>
            )}
          </Col>
        </Row>

        {/* 提交按钮 */}
        <div style={{ textAlign: 'right', marginTop: 24, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <Space>
            <Button onClick={() => navigate('/admin/products')}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditMode ? '更新商品' : '创建商品'}
            </Button>
          </Space>
        </div>
      </Form>

      {/* 图片预览Modal */}
      <div
        style={{
          display: previewVisible ? 'flex' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: 1000,
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={() => setPreviewVisible(false)}
      >
        <div style={{ maxWidth: '90%', maxHeight: '90%' }}>
          <img alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} src={previewImage} />
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
